import { fromSubmittedMatches, toSubmittedMatches } from '@athena/ui'

export interface LocalAnswer {
  selectedOptionId?: string
  textAnswer?: string
  /** ASSOCIATION: the option ids placed so far, in order. */
  submittedOrder?: readonly string[]
  /** MATCHING: left item id to the right item id put against it. */
  matches?: Readonly<Record<string, string>>
}

/** Question id to what the student has picked or typed so far. */
export type AnswerMap = Record<string, LocalAnswer>

/**
 * In-progress answers used to live in localStorage, mirrored by a Jotai atom.
 * They do not any more, and the reason is worth writing down.
 *
 * That design came from V1.md section 10, which said selection stays local
 * until submit because there was no mutation to save it — so a closed tab cost
 * nothing and the map was still there on the way back. There is a mutation
 * now. `saveAttempt` writes the answers and the position to the server, which
 * is the only place a second device can read them from, and keeping a local
 * copy beside it would mean two answers to "what did this student put", with the
 * stale one able to win.
 *
 * What keeps a closed tab from costing anything now is that the runner saves
 * as the student works (`useAttemptAutosave`): an answer reaches the server a
 * moment after it is given, the page says so when one has not, and leaving
 * with an answer still unsaved asks first. The server stays the only copy.
 */

export interface SubmittedAnswerPayload {
  questionId: string
  selectedOptionId?: string
  textAnswer?: string
  submittedOrder?: string[]
  matches?: Array<{ leftId: string; rightId: string }>
}

/**
 * Only the questions the student actually answered.
 *
 * A blank is scored as wrong and stays in the denominator (V1.md section 10),
 * so there is nothing to send for one. An entry carrying neither an option nor
 * text would also be a wrong-shaped answer, and the scorer rejects the whole
 * submission for one of those rather than guessing.
 *
 * Exactly one carrier per answer, which is why these are `if`/`continue`
 * rather than a spread of whatever is set: the server reads "which field is
 * present" as "which kind of question this is", and two present fields is a
 * rejected submission rather than a helpful one.
 *
 * An ordering question with *some* options placed is sent as it stands. It is
 * not the authored order, so it scores wrong — but it is what the student did,
 * and it is what their results screen shows them afterwards. An ordering
 * question with nothing placed is a blank like any other and is left out.
 *
 * A matching question is the same, with one difference that matters: it is
 * marked pair by pair, so a half-finished board is worth the pairs it got
 * right rather than nothing at all.
 *
 * Saving uses the same shape, and the same omission means the same thing: a
 * question left out of a save is one the student cleared, and the server
 * deletes the row it had.
 */
export function toSubmittedAnswers(answers: AnswerMap): SubmittedAnswerPayload[] {
  const submitted: SubmittedAnswerPayload[] = []

  for (const [questionId, answer] of Object.entries(answers)) {
    if (answer.selectedOptionId) {
      submitted.push({ questionId, selectedOptionId: answer.selectedOptionId })
      continue
    }
    if (answer.submittedOrder && answer.submittedOrder.length > 0) {
      submitted.push({ questionId, submittedOrder: [...answer.submittedOrder] })
      continue
    }
    if (answer.matches && Object.keys(answer.matches).length > 0) {
      submitted.push({ questionId, matches: toSubmittedMatches(answer.matches) })
      continue
    }
    const text = answer.textAnswer?.trim()
    if (text) submitted.push({ questionId, textAnswer: text })
  }

  return submitted
}

/**
 * The parts of a question that say what a complete answer to it is — its
 * options and its two columns — and nothing that could say which is right,
 * because the student's copy of a question carries no such thing.
 */
export interface CompletableQuestion {
  id: string
  type: string
  options: ReadonlyArray<{ id: string }>
  leftItems: ReadonlyArray<{ id: string }>
  rightItems: ReadonlyArray<{ id: string }>
}

/**
 * Whether the student has given this question a complete answer.
 *
 * Asked before the practice is handed in, so the student can be told what is
 * missing. It is about finishing, not about being right, and it changes
 * nothing about what is sent: a half-arranged order and a half-matched board
 * still go to the server as they stand and are scored as they stand
 * (`toSubmittedAnswers`). They are just not *finished*, and a student about to
 * hand in should know that.
 *
 * Switched the way the runner decides what to render, so every question is
 * judged by the interaction the student was actually given:
 *
 * - **Matching** — every term on the left has one of this question's
 *   definitions against it.
 * - **Ordering** — every one of this question's options has been placed, each
 *   once.
 * - **Choice** (multiple choice, true/false) — one of this question's options
 *   is picked.
 * - **Short answer** — there is text, once trimmed. Whitespace alone is a blank,
 *   as it is to `toSubmittedAnswers`, which sends nothing for it.
 */
export function isQuestionAnswered(
  question: CompletableQuestion,
  answer: LocalAnswer | undefined,
): boolean {
  if (!answer) return false

  if (question.type === 'MATCHING') {
    const matches = answer.matches ?? {}
    const definitions = new Set(question.rightItems.map((item) => item.id))
    return (
      question.leftItems.length > 0 &&
      question.leftItems.every((item) => definitions.has(matches[item.id] ?? ''))
    )
  }

  if (question.type === 'ASSOCIATION') {
    const placed = answer.submittedOrder ?? []
    const options = new Set(question.options.map((option) => option.id))
    return (
      options.size > 0 &&
      placed.length === options.size &&
      new Set(placed).size === placed.length &&
      placed.every((id) => options.has(id))
    )
  }

  if (question.options.length > 0) {
    return question.options.some((option) => option.id === answer.selectedOptionId)
  }

  return (answer.textAnswer?.trim() ?? '') !== ''
}

/** Where the questions without a complete answer are, first to last. */
export function unansweredPositions(
  questions: readonly CompletableQuestion[],
  answers: AnswerMap,
): number[] {
  return questions.flatMap((question, position) =>
    isQuestionAnswered(question, answers[question.id]) ? [] : [position],
  )
}

/** What the server saved, back into the shape the runner edits. */
export function fromSavedAnswers(
  saved: ReadonlyArray<{
    questionId: string
    selectedOptionId?: string | null
    textAnswer?: string | null
    submittedOrder?: readonly string[] | null
    matches?: ReadonlyArray<{ leftId: string; rightId: string }> | null
  }>,
): AnswerMap {
  const answers: AnswerMap = {}

  for (const answer of saved) {
    answers[answer.questionId] = {
      ...(answer.selectedOptionId ? { selectedOptionId: answer.selectedOptionId } : {}),
      ...(answer.textAnswer ? { textAnswer: answer.textAnswer } : {}),
      ...(answer.submittedOrder && answer.submittedOrder.length > 0
        ? { submittedOrder: answer.submittedOrder }
        : {}),
      ...(answer.matches && answer.matches.length > 0
        ? { matches: fromSubmittedMatches(answer.matches) }
        : {}),
    }
  }

  return answers
}
